<template>
    <div>
        <Modal size="full" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">Make payment request</div>
            </template>

            <template #body>
                <div class="justify-between border-t border-gray-200 p-6">
                    <div class="flex gap-2">
                        <SelectInput
                            v-model="v$.type.$model"
                            placeholder="Select a target"
                            class="my-6 w-52 sm:w-80"
                            label="Target"
                            :data="[
                                {
                                    title: 'PRO',
                                    value: 'pro',
                                },
                                {
                                    title: 'Plugin',
                                    value: 'plugin',
                                },
                            ]"
                        />

                        <TextInput
                            placeholder="Target ID"
                            :model-value="team.id"
                            class="my-6 w-52 sm:w-80"
                            label="Target ID"
                            readonly
                        />
                    </div>

                    <div class="flex gap-2">
                        <TextInput
                            v-model="v$.name.$model"
                            required
                            placeholder="Name"
                            :error="getError('url')"
                            class="my-6 w-52 sm:w-80"
                            label="Name"
                        />

                        <TextInput
                            v-model="v$.dueDate.$model"
                            required
                            placeholder="Due date"
                            class="my-6 w-52 sm:w-80"
                            label="Due date"
                            type="date"
                        />

                        <TextInput
                            v-model="v$.taxableDate.$model"
                            placeholder="Taxable date"
                            class="my-6 w-52 sm:w-80"
                            label="Taxable date"
                            type="date"
                        />
                    </div>

                    <div>
                        <CheckboxInput
                            v-model:checked="state.initialSubscriptionEnabled"
                            name="Inital subscription enabled"
                            class="my-6 w-52 sm:w-80"
                            label="Create recurring subscription renewal after payment and send automatically"
                            size="lg"
                            @change="onInitialSubscriptionToggled"
                        />

                        <SelectInput
                            v-if="state.initialSubscriptionEnabled"
                            v-model="v$.initialSubscriptionPlanId.$model"
                            required
                            class="my-6 w-52 sm:w-80"
                            label="Subscription plan"
                            :data="subscriptionPlans"
                            placeholder="Select a subscription plan"
                            @update:model-value="initialSubscriptionPlanIdSet"
                        />
                    </div>

                    <div class="mt-4 rounded-md bg-white p-4 shadow-sm">
                        <div class="text-lg font-medium text-gray-900">
                            Items
                        </div>

                        <div
                            v-for="(item, index) in state.items"
                            :key="index"
                            class="mt-4 grid grid-cols-6 items-end gap-x-5"
                        >
                            <TextInput
                                v-model="item.name"
                                label="Name"
                                required
                                :disabled="state.initialSubscriptionEnabled"
                            />
                            <SelectInput
                                v-model="item.pricing_list_item_id"
                                required
                                label="Pricing list item"
                                :data="pricingListItems"
                                :disabled="state.initialSubscriptionEnabled"
                                placeholder="Select a pricing list item"
                            />
                            <TextInput
                                v-model="item.price"
                                required
                                label="Price"
                                type="number"
                                :disabled="state.initialSubscriptionEnabled"
                            />
                            <TextInput
                                v-model="item.quantity"
                                required
                                label="Quantity"
                                type="number"
                                :disabled="state.initialSubscriptionEnabled"
                            />
                            <TextInput
                                :model-value="item.price * item.quantity"
                                required
                                label="Total"
                                readonly
                                type="number"
                            />

                            <AtomicButton
                                class="w-10"
                                color="white"
                                size="md"
                                @click="state.items.splice(index, 1)"
                            >
                                <template #icon>
                                    <TrashIcon class="h-6 w-6" />
                                </template>
                            </AtomicButton>
                        </div>

                        <div class="mt-4">
                            <AtomicButton
                                color="white"
                                size="md"
                                :disabled="state.initialSubscriptionEnabled"
                                @click="
                                    state.items.push({
                                        name: '',
                                        price: 0,
                                        quantity: 1,
                                        pricing_list_item_id: 'TOP-DATA-21',
                                    })
                                "
                            >
                                Add item
                            </AtomicButton>
                        </div>

                        <div class="flex justify-end">
                            <div class="divide-y divide-gray-200">
                                <div
                                    class="text-base font-medium text-gray-600"
                                >
                                    Subtotal: ${{ subtotal }}
                                </div>

                                <div class="text-lg font-medium text-gray-600">
                                    Total (with tax): ${{ total }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div
                    class="flex justify-end gap-3 border-t border-gray-200 p-6"
                >
                    <AtomicButton
                        color="yellow"
                        size="md"
                        :disabled="loading || v$.$invalid"
                        @click="createPaymentRequest()"
                    >
                        Make payment request
                    </AtomicButton>

                    <AtomicButton
                        color="white"
                        size="md"
                        @click="emit('close')"
                    >
                        Cancel
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import { TrashIcon } from '@heroicons/vue/24/outline';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import useVuelidate from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

const emit = defineEmits(['close']);

const props = defineProps<{
    team: ICurrentTeam;
}>();

interface Item {
    name: string;
    price: number;
    quantity: number;
    pricing_list_item_id?: string;
}

const subscriptionPlans = ref([]);
const loading = ref(false);

const state = reactive({
    type: 'pro',
    name: '',
    dueDate: '',
    taxableDate: '',
    initialSubscriptionEnabled: false,
    initialSubscriptionPlanId: 0,
    items: [
        {
            name: '',
            price: 0,
            quantity: 1,
            pricing_list_item_id: 'TOP-DATA-21',
        },
    ] as Item[],
});

const total = ref(0);

const subtotal = computed(() => {
    return state.items.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
    );
});

watch(subtotal, (newSubtotal) => {
    getTotalPriceWithTax(newSubtotal);
});

watch(
    () => state.type,
    () => {
        getTotalPriceWithTax(subtotal.value);
    }
);

const getTotalPriceWithTax = async (subtotal: number) => {
    try {
        const result = await axios.get(route('kiosk.price-with-tax.index'), {
            params: {
                price: subtotal,
                type: state.type,
                team_id: props.team.id,
            },
        });
        total.value = result.data.total;
    } catch (error) {
        return;
    }
};

const rules = {
    type: {
        required,
    },
    name: {
        required,
    },
    dueDate: {
        required,
    },
    taxableDate: {
        validTaxableDate: helpers.withMessage(
            'Taxable date must be a valid date, no later than due date, and not more than 14 days before it.',
            (value: any, { dueDate }) => {
                if (!value) return true; // Allow empty value
                const taxableDate = new Date(value);
                const dueDateValue = new Date(dueDate);
                if (isNaN(taxableDate) || isNaN(dueDateValue)) return false; // Must be valid dates
                return (
                    taxableDate <= dueDateValue &&
                    taxableDate >=
                        new Date(
                            dueDateValue.setDate(dueDateValue.getDate() - 14)
                        )
                );
            }
        ),
    },
    initialSubscriptionPlanId: {
        required: (value: number) =>
            state.initialSubscriptionEnabled ? value !== 0 : true,
    },
    items: {
        required: (value: Item[]) => value.length > 0,
        validItems: helpers.withMessage(
            'Each item must have a name, a positive price, quantity of at least 1, and a pricing list item ID.',
            (value: Item[]) => {
                return value.every(
                    (item) =>
                        (state.initialSubscriptionEnabled
                            ? true
                            : !!item.name) &&
                        (state.initialSubscriptionEnabled
                            ? true
                            : item.price > 0) &&
                        item.quantity > 0 &&
                        (state.initialSubscriptionEnabled
                            ? true
                            : !!item.pricing_list_item_id)
                );
            }
        ),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

const pricingListItems = ref([]);

const createPaymentRequest = async function () {
    v$.value.$touch();

    if (v$.value.$error || subtotal.value <= 0) {
        return;
    } else {
        const data = {
            type: state.type,
            team_id: props.team.id,
            name: state.name,
            due_date: state.dueDate,
            taxable_date: state.taxableDate,
            items: [] as Item[],
            initial_subscription_plan_id: state.initialSubscriptionEnabled
                ? state.initialSubscriptionPlanId
                : null,
        };

        state.items.forEach((item) => {
            data.items.push({
                name: item.name || 'Default item',
                price: item.price,
                quantity: item.quantity,
                pricing_list_item_id: item.pricing_list_item_id,
            });
        });

        try {
            loading.value = true;
            await axios.post(route('kiosk.payment-requests.store'), data);

            create({
                title: 'Payment request created',
                type: 'success',
            });

            emit('close');
        } catch (error: any) {
            create({
                title: 'Error',
                text:
                    error.message ??
                    'An error occurred while creating the payment request.',
                type: 'error',
            });
        } finally {
            loading.value = false;
        }
    }
};

const onInitialSubscriptionToggled = function () {
    if (state.initialSubscriptionEnabled) {
        state.items = [
            {
                name: 'Default name',
                price: 0,
                quantity: 1,
                pricing_list_item_id: undefined,
            },
        ];
    }
};

const initialSubscriptionPlanIdSet = function (planId: number) {
    const plan = subscriptionPlans.value.find((plan) => plan.value === planId);

    if (plan) {
        state.items = [
            {
                name: plan.name,
                price: plan.price / 100,
                quantity: 1,
                pricing_list_item_id: plan.invoice_item,
            },
        ];

        state.type = plan.type;
    }
};

const getPricingListItems = function () {
    axios.get(route('kiosk.pricing-list-items.index')).then((response) => {
        pricingListItems.value = response.data.data;
    });
};

const getSubscriptionPlans = function () {
    let fetchedSubscriptionPlans = [];

    Promise.all([
        axios.get(route('kiosk.subscription-plans.index'), {
            params: {
                type: 'pro',
            },
        }),
        axios.get(route('kiosk.subscription-plans.index'), {
            params: {
                type: 'plugin',
            },
        }),
    ])
        .then((responses) => {
            fetchedSubscriptionPlans = responses[0].data.data.concat(
                responses[1].data.data
            );

            subscriptionPlans.value = fetchedSubscriptionPlans.map(
                (fetchedSubscriptionPlan) => {
                    return {
                        title: fetchedSubscriptionPlan.uuid,
                        value: fetchedSubscriptionPlan.id,
                        price: fetchedSubscriptionPlan.price,
                        type: fetchedSubscriptionPlan.type,
                        name: fetchedSubscriptionPlan.name,
                        invoice_item:
                            fetchedSubscriptionPlan.invoice_pricing_list_item,
                    };
                }
            );
        })
        .catch((error) => {
            console.error('Error fetching subscription plans:', error);
        });
};

onMounted(() => {
    getPricingListItems();
    getSubscriptionPlans();
});
</script>
